import { Col, Container, Row } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { observer } from 'mobx-react'

import { OrderDetailsCard } from 'Modules/orders/components/OrderDetailsCard/OrderDetailsCard'
import { OrderPresentationCard } from 'Modules/orders/components/OrderPresentationCard/OrderPresentationCard'

import { EmployeeEntityCard } from 'Components/EmployeeEntityCard/EmployeeEntityCard'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageHeader } from 'Components/PageHeader/PageHeader'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'
import { ShippingAddressCard } from 'Components/ShippingAddressCard/ShippingAddressCard'

import { useActiveUser } from 'Hooks'

import { getFullContactName } from 'Utilities'

import {
  getCaseItemDetails,
  getCaseItemDetailsVariables,
} from 'Constants/graphql/queries/detail/__generated__/getCaseItemDetails'
import { GET_CASE_ITEM_DETAILS } from 'Constants/graphql/queries/detail/GetCaseItemDetails'
import { PortalSectionEnum } from 'Constants/portalSection'

import { getCaseItemTypeTranslationId } from 'Portal/src/utilities/getCaseItemTypeTranslationId/getCaseItemTypeTranslationId.util'

import { shouldShowTracking } from './Order.utils'

export const Order = observer(() => {
  const { activeOrganisation } = useActiveUser()
  const isOrganisation = Boolean(activeOrganisation?.id)
  const { employeeId, organisationId, orderId } = useParams<{
    employeeId: string
    organisationId: string
    orderId: string
  }>()

  const {
    loading: isLoading,
    data,
    error,
  } = useQuery<getCaseItemDetails, getCaseItemDetailsVariables>(
    GET_CASE_ITEM_DETAILS,
    {
      variables: { employeeId, id: orderId.toString(), organisationId },
    },
  )

  if (isLoading) {
    return <PageLoadingState />
  }

  if (error) {
    return <PageErrorState />
  }

  if (!data) {
    return <PageErrorState />
  }

  const { caseItem } = data
  const title = getCaseItemTypeTranslationId(caseItem.caseType)

  return (
    <Container className="pt-6" fluid>
      <div>
        <PageHeader portalSection={PortalSectionEnum.OrderDetails} />
      </div>

      <Row>
        <Col xxl={8}>
          <OrderPresentationCard
            deviceDefinition={caseItem.data.deviceDefinition}
            requestedOfferOrder={caseItem.data.requestedOfferOrder}
            requestedRentCategory={caseItem.data.requestedRentCategory}
            status={caseItem.status}
          />

          <OrderDetailsCard caseItem={caseItem} title={title} />
        </Col>

        <Col xxl={4}>
          <Row>
            <Col lg={6} xs={12} xxl={12}>
              {caseItem.data.requestedShippingAddress && (
                <ShippingAddressCard
                  city={caseItem.data.requestedShippingAddress.city}
                  country={caseItem.data.requestedShippingAddress.country}
                  employee={getFullContactName(
                    caseItem.assignedEmployee?.contact,
                  )}
                  name={caseItem.data.requestedShippingAddress.name}
                  nameAddition={
                    caseItem.data.requestedShippingAddress.nameAddition
                  }
                  shipping={caseItem.shipping}
                  showTracking={shouldShowTracking(caseItem)}
                  street={caseItem.data.requestedShippingAddress.street}
                  streetAddition={
                    caseItem.data.requestedShippingAddress.streetAddition
                  }
                  trackingURL={caseItem.shipping?.trackingURL}
                  zipcode={caseItem.data.requestedShippingAddress.zipcode}
                />
              )}
            </Col>

            <Col lg={6} xs={12} xxl={12}>
              {isOrganisation && (
                <EmployeeEntityCard
                  employee={caseItem.assignedEmployee ?? null}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  )
})
